import * as React from 'react';
import { useState } from 'react';
import reactHtmlParser from 'react-html-parser';
import { updateRecords, setAutoThreadingDefault, autoThreadingToggled } from '../../actions/mail';
import { EmailStatus } from './EmailStatus';
import { batch, useDispatch, useSelector } from 'react-redux';
import { changeAlertText /* isLoading */ } from '../../actions/index';
import { CalendarStatus } from './CalendarStatus';
// import { setEventStatus, unsyncEvent } from '../../actions/calendar';
// import * as CalendarApi from '../../../api/emailsApi/calendar';
import calendarEventStatus from '../../utils/calendarEventStatus';
import { Checkbox } from '@salesforce/design-system-react';
import Svg from '../shared/Svg';
import colors from '../../utils/colors';
import { sortRecordsAlphabetically } from '../../utils/selectedRecordsUtils';
import './StatusBar.scss';

export const StatusBar = () => {
  const dispatch = useDispatch();
  const dropdownRef = React.useRef(null);
  const settings = useSelector((state) => state.mail.settings);
  const isEmail = useSelector((state) => state.app.isEmail);
  const isComposingEmail = useSelector((state) => state.mail.isComposingEmail);
  const autoThreadingWasToggled = useSelector((state) => state.mail.autoThreadingToggled);
  const eventStatus = useSelector((state) => state.calendar.eventStatus);
  const selectedRecords = useSelector((state) => state.mail.selectedRecords);
  const autoThreadingDefault = useSelector((state) => state.mail.autoThreadingDefault);
  const initialAutothreadedRecords = useSelector((state) => state.mail.initialAutothreadedRecords);
  const authUser = useSelector((state) => state.auth?.user);
  const delegatedCalendarOwner = useSelector((state) => state.calendar?.ownerForDelegatedCalendar);
  const [autoThreadingDropdownOpen, setAutoThreadingDropdownOpen] = useState(false);

  const objectsAndDisplayFields = settings?.objectsAndDisplayFields;
  const userCanSetAutoThreading = settings?.autoThreading?.enablePerUserBasis;
  // This is meant for a very specific scenario in order to properly show whether a compose mode email has autothreading enabled.
  const presaveInitialEmailHadAutothreading = (isComposingEmail && !autoThreadingWasToggled && !autoThreadingDefault && initialAutothreadedRecords?.length > 0);


  const numberOfSelectedRecords = Object.keys(selectedRecords).length;
  const recordsWithAutothreading = Object.values(selectedRecords).filter((record) => record.autothreading);
  const allRecordsAreAutothreaded = recordsWithAutothreading.length === numberOfSelectedRecords;

  const sortedRecords = sortRecordsAlphabetically(selectedRecords, objectsAndDisplayFields);

  const handleAutoThreadingButtonClick = () => {
    const recordsToUpdate = recordsWithAutothreading.length ? recordsWithAutothreading : Object.values(selectedRecords);

    batch(() => {
      dispatch(autoThreadingToggled(true));
      dispatch(setAutoThreadingDefault(numberOfSelectedRecords ? !recordsWithAutothreading.length : !autoThreadingDefault));
      dispatch(updateRecords(recordsToUpdate.map((record) => ({ ...record, autothreading: numberOfSelectedRecords ? !recordsWithAutothreading.length : !autoThreadingDefault }))));
      dispatch(changeAlertText(`Auto-threading is now ${(numberOfSelectedRecords ? !recordsWithAutothreading.length : !autoThreadingDefault) ? 'on' : 'off'}`));
    });
  };

  // Commenting out until unsync functionality is finalized to appease Linter
  // const unsyncCalendarEvent = async () => {
  //   try {
  //     dispatch(isLoading(true));
  //     dispatch(setEventStatus(calendarEventStatus.deleting));
  //     await CalendarApi.unSyncEvent(eventId);
  //     dispatch(isLoading(false));
  //     dispatch(unsyncEvent());
  //     dispatch(changeAlertText('Calendar event not saved.'));
  //   } catch (error) {
  //     console.error('Error unsyncing event', error);
  //     dispatch(isLoading(false));
  //     dispatch(setEventStatus(calendarEventStatus.saved));
  //     dispatch(changeAlertText('Error unsyncing event', error));
  //   }
  // };
  const autoThreadingButtonsEnabled = userCanSetAutoThreading && (numberOfSelectedRecords > 0);

  const useClickOutside = (ref, onClickOutside) => {
    React.useEffect(() => {
      const handleClickOutside = (event) => {
        // If the dropdown is open and the click is outside of the dropdown, close the dropdown unless the click is on the dropdown button
        if (ref.current && !ref.current.contains(event.target) && autoThreadingDropdownOpen && !event.target.closest('.status-autoThreadingArrow')) {
          onClickOutside();
        }
      };
      // Bind
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // dispose
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref, onClickOutside]);
  };

  useClickOutside(dropdownRef, () => setAutoThreadingDropdownOpen(false));
  return isEmail ? (
    <div className='status'>
      <>
        <EmailStatus />
        <div id='autothreading-status' className='status-autoThreading'>
          <div>
            <div className={(autoThreadingDefault && !numberOfSelectedRecords) || recordsWithAutothreading.length || (presaveInitialEmailHadAutothreading && sortedRecords?.length === 0) ? 'status-autoThreadingIconGreen' : 'status-autoThreadingIconRed'} />
          </div>
          Autothreading:
          <div className={userCanSetAutoThreading ? 'status-autoThreadingButton' : 'status-autoThreadingButton-disabled'}
            onClick={() => userCanSetAutoThreading && handleAutoThreadingButtonClick()}>
            {(autoThreadingDefault && !numberOfSelectedRecords) || recordsWithAutothreading.length || (presaveInitialEmailHadAutothreading && sortedRecords?.length === 0) ? 'On' : 'Off'}
          </div>
          {
            !!numberOfSelectedRecords && <div className='status-autoThreadingCount'>{`(${recordsWithAutothreading.length}/${numberOfSelectedRecords})`}</div>
          }
          <div>
            <button className='status-autoThreadingArrow' disabled={autoThreadingButtonsEnabled ? false : true} onClick={() => setAutoThreadingDropdownOpen(!autoThreadingDropdownOpen)}>
              <Svg data={'assets/chevronFull.svg'} fill={autoThreadingButtonsEnabled ? colors.darkgrey : colors.lightGrey} />
            </button>
          </div>
        </div>
        {autoThreadingDropdownOpen && (
          <div ref={dropdownRef} className='status-autoThreadingDropdown'>
            <div className='status-autothreadingDropdownItemHeader'>
              <Checkbox
                labels={{ label: 'Select All' }}
                indeterminate={(recordsWithAutothreading.length > 0) && (recordsWithAutothreading.length < numberOfSelectedRecords)}
                checked={allRecordsAreAutothreaded}
                onChange={(e) => {
                  dispatch(updateRecords(sortedRecords.map((record) => ({ ...record, autothreading: e.target.checked }))));
                  dispatch(autoThreadingToggled(true));
                }} />
            </div>
            {sortedRecords?.map((record) => (
              <div key={record.Id} className='status-autothreadingDropdownItem'>
                <Checkbox
                  id={record.Id}
                  labels={{ label: reactHtmlParser(record[objectsAndDisplayFields?.find((object) => object.apiName === record.type)?.displayFields?.[0]?.apiName] || '') }} // can/should we cache this somehow?
                  checked={record.autothreading}
                  onChange={(e) => {
                    dispatch(updateRecords([{ ...record, autothreading: e.target.checked }]));
                    dispatch(autoThreadingToggled(true));
                  }} />
              </div>
            ))}
          </div>
        )}
      </>
    </div>
  ) : (
    <>
      <div className='status'>
        <>
          <CalendarStatus />
          {eventStatus && eventStatus !== calendarEventStatus.deleting && <div id='autothreading-status' className='status-autoThreading'>
            {/*
          Hiding unsync button until unsync functionality is fully built
          <div className="status-unsyncButton"
            onClick={unsyncCalendarEvent}>
            Remove from Litify
          </div>
          */}
          </div>}
        </>
      </div>
      {authUser?.email !== delegatedCalendarOwner?.emailAddress && delegatedCalendarOwner?.emailAddress ?
        <p className='status-delegateUserBanner'>Calendaring as {delegatedCalendarOwner.displayName || delegatedCalendarOwner.emailAddress}</p> : <></>
      }
    </>
  );
};
